import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// Tab/screen capture recorder: records the shared tab (visuals + audio) to a downloadable .webm.
export function useReelRecorder(filename) {
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanup = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Your browser doesn't support recording. Try Chrome or Edge on desktop.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true,
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const preferred = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
      ].find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: preferred });
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.webm`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        cleanup();
        toast.success("Recording saved to your downloads.");
      };
      // If the user ends the share from the browser bar, stop & save too.
      stream.getVideoTracks()[0].addEventListener("ended", () => stop());
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      return true;
    } catch (e) {
      // User cancelled the share picker, or permission denied.
      cleanup();
      return false;
    }
  };

  useEffect(() => () => cleanup(), []);

  return { recording, start, stop };
}
